import { useEffect } from 'react';
import { useMutation } from 'platform/useMutation';
import { gql, MutationHookOptions } from '@apollo/client';

import { ClosingTaskSubmissionEventName } from 'sellDirect/graphql/useUpdateClosingTasks';
import {
  ChangeCloseEscrowDate,
  ChangeCloseEscrowDateVariables,
} from 'sellDirect/graphql/__generated__/ChangeCloseEscrowDate';
import { useObservability } from 'shared/helpers/observability';
import { useOfferContext } from 'sellDirect/OfferContext';

const ChangeCloseEscrowDateMutation = gql`
  mutation ChangeCloseEscrowDate($webOfferToken: String!, $closeEscrowDate: String!) {
    seller {
      changeCloseEscrowDate(webOfferToken: $webOfferToken, closeEscrowDate: $closeEscrowDate)
    }
  }
`;

/**
 * Hook that allows updating the close of escrow date
 *
 * @param onCompleted - Optional callback to call after the request late checkout request completes
 * @param onError - Optional callback to call if there's an error making the request
 * @param errorPolicy - Optional error policy to use
 */
export const useUpdateCloseEscrowDate = (options?: MutationHookOptions<ChangeCloseEscrowDate>) => {
  const { trackEvent, trackError } = useObservability();
  const { offer } = useOfferContext();

  const onUpdateSuccess = (data: ChangeCloseEscrowDate) => {
    trackEvent(ClosingTaskSubmissionEventName, 'change-of-escrow-date');
    options?.onCompleted && options?.onCompleted(data);
  };

  const [update, { loading, error }] = useMutation<
    ChangeCloseEscrowDate,
    ChangeCloseEscrowDateVariables
  >(ChangeCloseEscrowDateMutation, {
    onCompleted: onUpdateSuccess,
    onError: options?.onError,
    awaitRefetchQueries: true,
    errorPolicy: options?.errorPolicy,
    fetchPolicy: 'no-cache',
    refetchQueries: ['GetSellDirect'],
  });

  const updateCloseEscrowDate = (closeEscrowDate: string | undefined) => {
    if (!offer?.leadToken) {
      trackError('missing-web-offer-token');
      return;
    }

    if (!closeEscrowDate) {
      trackError('missing-close-escrow-date');
      return;
    }

    // updates close escrow date & generates change of escrow addendum
    update({
      variables: {
        webOfferToken: offer.leadToken,
        closeEscrowDate,
      },
    }).catch((error) => {
      trackError('request-change-close-escrow-date', error);
    });
  };

  useEffect(() => {
    if (error) {
      trackError('request-change-close-escrow-date', error);
    }
    // eslint-disable-next-line
  }, [error]);

  return { updateCloseEscrowDate, loading, error };
};
